import React from 'react';
import { Side } from './model';
import RoundHeaderBand from './RoundHeaderBand';

export function winningPathLength(game, visited = {}) {
  if (!game?.id) return 0;
  if (visited[game.id]) return 0;
  visited[game.id] = true;
  const sides = game.sides ? Object.values(game.sides) : [];
  let maxDepth = 0;
  for (const sideInfo of sides) {
    const seed = sideInfo.seed;
    if (seed && seed.sourceGame != null && Number(seed.rank) === 1) {
      maxDepth = Math.max(maxDepth, winningPathLength(seed.sourceGame, visited));
    }
  }
  return 1 + maxDepth;
}

/**
 * Árbol de bracket basado en react-tournament-bracket, con LineComponent real.
 * Soporta `mirror` para pintar la mitad derecha de un cuadro a dos lados:
 * con mirror, las rondas alimentadoras crecen hacia la derecha en vez de a la izquierda.
 */
export function toBracketGames({
  GameComponent,
  game,
  x,
  y,
  gameDimensions,
  roundSeparatorWidth,
  round,
  lineInfo,
  homeOnTop,
  LineComponent,
  mirror = false,
  ...rest
}) {
  const { width: gameWidth, height: gameHeight } = gameDimensions;
  const ySep = gameHeight * Math.pow(2, round - 2);
  const Connector = LineComponent || 'path';

  const feederBlocks = Object.entries(game.sides || {})
    .map(([side, sideInfo]) => ({ ...sideInfo, side }))
    .filter(({ seed }) => seed && seed.sourceGame != null && Number(seed.rank) === 1)
    .map(({ seed: { sourceGame }, side }) => {
      const isTop = side === Side.HOME ? homeOnTop : !homeOnTop;
      const multiplier = isTop ? -1 : 1;
      const rowY = y + gameHeight / 2 + lineInfo.yOffset;
      const startY = rowY + multiplier * lineInfo.homeVisitorSpread;
      const midY = rowY + (ySep / 2) * multiplier;

      const pathD = mirror
        ? [
            `M${x + gameWidth + lineInfo.separation} ${startY}`,
            `H${x + gameWidth + roundSeparatorWidth / 2}`,
            `V${midY}`,
            `H${x + gameWidth + roundSeparatorWidth + lineInfo.separation}`,
          ].join(' ')
        : [
            `M${x - lineInfo.separation} ${startY}`,
            `H${x - roundSeparatorWidth / 2}`,
            `V${midY}`,
            `H${x - roundSeparatorWidth + lineInfo.separation}`,
          ].join(' ');

      const key = `${game.id}-${side}-${y}-path`;
      const connectorEl =
        LineComponent != null ? (
          <Connector key={key} d={pathD} />
        ) : (
          <path key={key} d={pathD} fill="transparent" stroke="black" />
        );

      const feederX = mirror
        ? x + gameWidth + roundSeparatorWidth
        : x - gameWidth - roundSeparatorWidth;

      return [
        connectorEl,
        ...toBracketGames({
          GameComponent,
          game: sourceGame,
          homeOnTop,
          lineInfo,
          gameDimensions,
          roundSeparatorWidth,
          x: feederX,
          y: y + (ySep / 2) * multiplier,
          round: round - 1,
          LineComponent,
          mirror,
          ...rest,
        }),
      ];
    });

  return [
    <g key={`${game.id}-${y}`}>
      <GameComponent
        {...rest}
        {...gameDimensions}
        key={game.id}
        homeOnTop={homeOnTop}
        game={game}
        x={x}
        y={y}
      />
    </g>,
    ...feederBlocks.flat(),
  ];
}

export const defaultGameDimensions = { height: 80, width: 200 };

/** Mantener en sync con thirdPlaceGeometry de bracketRounds.js. */
function thirdPlaceCardY(gameH, finalYAbs) {
  return finalYAbs + gameH + Math.round(gameH * 0.5);
}

/**
 * Cuadro a una sola columna (final a la derecha).
 * Si recibe `headerColumns` + `bandHeight`, pinta arriba la banda de fases.
 * `thirdPlaceGame`: partido de 3º/4º puesto, se pinta debajo de la final.
 */
export default function BracketTree(props) {
  const {
    GameComponent,
    game,
    gameDimensions = defaultGameDimensions,
    svgPadding = 20,
    roundSeparatorWidth = 24,
    homeOnTop = true,
    lineInfo = { yOffset: -6, separation: 6, homeVisitorSpread: 11 },
    children,
    LineComponent,
    className,
    svgProps,
    headerColumns = null,
    bandHeight = 0,
    thirdPlaceGame = null,
    ...rest
  } = props;

  const { width: gameW, height: gameH } = gameDimensions;
  const numRounds = winningPathLength(game);
  const innerHeight = gameH * Math.pow(2, numRounds - 1) + svgPadding * 2;
  const svgWidth = numRounds * (gameW + roundSeparatorWidth) + svgPadding * 2;

  const games = toBracketGames({
    GameComponent,
    gameDimensions,
    roundSeparatorWidth,
    game,
    round: numRounds,
    homeOnTop,
    lineInfo,
    LineComponent,
    x: svgWidth - svgPadding - gameW,
    y: innerHeight / 2 - gameH / 2,
    ...rest,
  });

  const finalX = svgWidth - svgPadding - gameW;
  const finalYAbs = bandHeight + innerHeight / 2 - gameH / 2;

  let svgHeight = innerHeight + bandHeight;
  let thirdPlaceNode = null;
  if (thirdPlaceGame) {
    const tpY = thirdPlaceCardY(gameH, finalYAbs);
    svgHeight = Math.max(svgHeight, tpY + gameH + svgPadding);
    thirdPlaceNode = (
      <g key="third-place">
        <GameComponent {...rest} {...gameDimensions} game={thirdPlaceGame} x={finalX} y={tpY} homeOnTop={homeOnTop} />
      </g>
    );
  }

  const w = svgWidth;
  const h = svgHeight;
  const raw = svgProps || {};
  const { style: incomingStyle, width: _ignoreW, height: _ignoreH, ...restSvgAttrs } = raw;
  const mergedStyle =
    incomingStyle && typeof incomingStyle === 'object' && !Array.isArray(incomingStyle)
      ? { ...incomingStyle }
      : {};
  delete mergedStyle.width;
  delete mergedStyle.height;

  const showBand = bandHeight > 0 && headerColumns;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      {...restSvgAttrs}
      width={w}
      height={h}
      style={mergedStyle}
      className={className}
    >
      {showBand ? <RoundHeaderBand columns={headerColumns} bandHeight={bandHeight} width={w} /> : null}
      <g transform={bandHeight > 0 ? `translate(0,${bandHeight})` : undefined}>{games}</g>
      {thirdPlaceNode}
      {children}
    </svg>
  );
}
